'use client';

import { IconForwardWhite } from '@mv/icons/web';
import { Heading, Pill } from '@mv/web-ui';
import { cva } from 'class-variance-authority';
import { useDataTagsFactory } from '@/modules/tracking/analytics/useDataTags';
import Link from '@/payload/components/Link/Link';
import RichText from '@/payload/components/RichText/RichText';
import type { CtaList as Props } from '@/payload/types';
import styles from './CtaList.module.scss';

const ctaGroupsClass = cva(styles.columns, {
	variants: {
		columns: {
			one: styles.oneColumn,
			two: styles.twoColumns,
		},
	},
	defaultVariants: {
		columns: 'one',
	},
});

const CtaList = ({ blockType, ctaGroups, text }: Props) => {
	const { createDataTags } = useDataTagsFactory();

	if (!ctaGroups) return;

	const columnsVariant = ctaGroups.length === 2 ? 'two' : 'one';

	return (
		<div className={styles.ctaList} data-component="CtaListBlock">
			<div className={ctaGroupsClass({ columns: columnsVariant })}>
				{ctaGroups.map((group, index) => {
					const { blockRef, labelRef } = createDataTags(blockType, group.id ?? index);
					return (
						<section key={group.id} ref={blockRef} className={styles.ctaGroup}>
							{group.label && (
								<Heading tag="h3" styleAs="h6" ref={labelRef}>
									{group.label}
								</Heading>
							)}
							<ul className={styles.links}>
								{group.links?.map((link) => (
									<li key={link.id} className={styles.linkItem}>
										<Pill fullWidth className={styles.pill} asChild>
											<Link link={link.link} className={styles.link}>
												{link.link.label} <IconForwardWhite size="150" />
											</Link>
										</Pill>
									</li>
								))}
							</ul>
						</section>
					);
				})}
			</div>
			{text && <RichText className={styles.text} content={text} />}
		</div>
	);
};

export default CtaList;
